import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { useMatriculaStore, type MatriculaFormValues } from '@/stores/matriculaStore'
import { useDetalhesStore } from '@/stores/detalhesStore'

interface ItemDeContexto {
  /** Rótulo em negrito, à esquerda do valor, no painel do atendente. */
  title: string
  content: string
}

interface InfoDeContato {
  name?: string
  email?: string
  description?: string
}

interface JivoApi {
  setCustomData?: (dados: ItemDeContexto[]) => void
  setContactInfo?: (info: InfoDeContato) => void
  open?: () => void
}

export function abrirChatDoJivo(): void {
  window.jivo_api?.open?.()
}

declare global {
  interface Window {
    jivo_api?: JivoApi
    /** Chamado pelo widget toda vez que o visitante abre o chat. */
    jivo_onOpen?: () => void
  }
}

// O contexto vem de dois lugares que não se enxergam: o Layout (quem está logado, em que tela) e o
// formulário de matrícula, lá dentro da página. As duas metades ficam aqui, e NADA é enviado ao
// escrevê-las — o formulário muda a cada tecla, e uma chamada por tecla seria uso de API jogado
// fora. Quem envia é `jivo_onOpen`, uma vez por abertura do chat, que é o único instante em que os
// dados têm leitor.
let contextoBase: ItemDeContexto[] = []
let contextoDoFormulario: ItemDeContexto[] = []
let contatoAtual: InfoDeContato | null = null

function enviarParaOJivo(): void {
  window.jivo_api?.setCustomData?.([...contextoBase, ...contextoDoFormulario])
  if (contatoAtual) window.jivo_api?.setContactInfo?.(contatoAtual)
}

/**
 * Mantém o painel do atendente do Jivo com quem está do outro lado e o que ele está vendo.
 *
 * Registrar `jivo_onOpen` já no Layout, e não na abertura, importa: o handler tem de existir antes
 * do primeiro clique no widget.
 */
export function useContextoDoJivo(): void {
  const usuario = useAuthStore((s) => s.usuario)
  const parceiro = useAuthStore((s) => s.parceiro)
  const contexto = useContextoDaTela()

  const dados: ItemDeContexto[] = [
    ...(usuario ? [{ title: 'Usuário', content: `${usuario.nome} (CPF ${usuario.cpf})` }] : []),
    ...(parceiro ? [{ title: 'Parceiro', content: `${parceiro.nome} (id ${parceiro.id})` }] : []),
    ...contexto,
  ]

  const dadosSerializados = JSON.stringify(dados)

  useEffect(() => {
    contextoBase = JSON.parse(dadosSerializados)
  }, [dadosSerializados])

  useEffect(() => {
    contatoAtual = usuario
      ? {
          name: usuario.nome,
          email: usuario.email,
          description: parceiro ? `Parceiro: ${parceiro.nome}` : undefined,
        }
      : null
  }, [usuario, parceiro])

  useEffect(() => {
    window.jivo_onOpen = enviarParaOJivo
    return () => {
      window.jivo_onOpen = undefined
    }
  }, [])
}

const ROTULOS_DO_FORMULARIO: Record<keyof MatriculaFormValues, string> = {
  cpf: 'CPF',
  nome: 'Nome',
  dataNascimento: 'Nascimento',
  telefone: 'Telefone',
  email: 'E-mail',
  cnh: 'CNH',
  categoriaCnh: 'Categoria CNH',
  dataPrimeiraHabilitacao: '1ª habilitação',
  renach: 'RENACH',
  rg: 'RG',
  municipio: 'Município',
  ufDestino: 'UF de destino',
}

/**
 * Guarda o formulário de matrícula como está na tela, inclusive o que ainda não foi salvo em lugar
 * nenhum: se o parceiro chama o suporte no meio do preenchimento, é justamente o rascunho que
 * explica a dúvida.
 */
export function useFormularioDeMatriculaNoJivo(values: MatriculaFormValues): void {
  const valoresSerializados = JSON.stringify(values)

  useEffect(() => {
    const valoresAtuais: MatriculaFormValues = JSON.parse(valoresSerializados)
    contextoDoFormulario = Object.entries(ROTULOS_DO_FORMULARIO)
      .filter(([campo]) => (valoresAtuais[campo as keyof MatriculaFormValues] ?? '').trim() !== '')
      .map(([campo, title]) => ({
        title,
        content: valoresAtuais[campo as keyof MatriculaFormValues],
      }))
  }, [valoresSerializados])

  useEffect(() => {
    return () => {
      contextoDoFormulario = []
    }
  }, [])
}

function useContextoDaTela(): ItemDeContexto[] {
  const { pathname } = useLocation()
  const dadosDaMatricula = useContextoDaMatricula()
  const dadosDoAluno = useContextoDoAluno(pathname.split('/detalhes/')[1] ?? '')

  if (pathname.endsWith('/matricula')) {
    return [{ title: 'Tela', content: 'Matricular' }, ...dadosDaMatricula]
  }
  if (pathname.startsWith('/detalhes/')) {
    return [{ title: 'Tela', content: 'Detalhes do aluno' }, ...dadosDoAluno]
  }
  return [{ title: 'Tela', content: nomeDaTela(pathname) }]
}

/** Os dados do aluno vêm do Formik (`useFormularioDeMatriculaNoJivo`); aqui só o que é do store. */
function useContextoDaMatricula(): ItemDeContexto[] {
  const cursoSelecionado = useMatriculaStore((s) => s.cursoSelecionado)
  const cursosAdicionais = useMatriculaStore((s) => s.cursosAdicionais)
  const allCursos = useMatriculaStore((s) => s.allCursos)

  const nomes = [cursoSelecionado, ...cursosAdicionais]
    .map((id) => allCursos.find((curso) => curso.id === id)?.nome)
    .filter((nome): nome is string => nome != null)

  return nomes.length > 0 ? [{ title: 'Cursos', content: nomes.join(', ') }] : []
}

/** O id vem do pathname: `useParams` no Layout não enxerga o param de uma rota filha. */
function useContextoDoAluno(matriculaId: string): ItemDeContexto[] {
  const matricula = useDetalhesStore((s) => s.matricula)

  // O store guarda a última matrícula aberta; durante a troca de tela ela ainda é a anterior.
  if (!matricula || String(matricula.id) !== matriculaId) {
    return matriculaId ? [{ title: 'Matrícula', content: matriculaId }] : []
  }

  return [
    { title: 'Matrícula', content: `${matricula.matricula} (id ${matricula.id})` },
    { title: 'Aluno', content: `${matricula.aluno.nome} (CPF ${matricula.aluno.cpf})` },
    { title: 'Curso', content: matricula.curso.nome },
    { title: 'Status DETRAN', content: `${matricula.uf} — ${matricula.statusDetran}` },
  ]
}

function nomeDaTela(pathname: string): string {
  if (pathname.startsWith('/dashboard')) return 'Painel'
  if (pathname.startsWith('/alunos')) return 'Alunos'
  if (pathname.startsWith('/turmas')) return 'Turmas'
  if (pathname.startsWith('/precos')) return 'Preços'
  if (pathname.startsWith('/configuracoes/usuarios')) return 'Usuários do parceiro'
  if (pathname.startsWith('/configuracoes')) return 'Configurações'
  if (pathname.startsWith('/selecionar-parceiro')) return 'Selecionar parceiro'
  if (pathname.startsWith('/contrato')) return 'Contrato'
  return pathname
}
